// The map's look in Blueprint (the maps design v3, place 7): muted roads, no shops, bus stops or icons,
// quiet labels, so our pens read on top. Every colour is a Blueprint token, taken from the token set
// itself, so the map can't drift from the app's light and dark. On the web it's Google's style array,
// set without a Map ID (a Map ID would turn the style array off, and make the iPad's maps billable).
// The stand-in (GoogleMap with no key) draws its ground in the same colours, from GoogleMap.css's
// --googlemap-* tokens, which map-style.test.ts holds to MAP_COLOURS.
import { blueprint } from '@flowology/tokens';

export type MapTheme = 'light' | 'dark';

/** The parts of the map that carry a colour. */
export type MapPart =
  | 'land'
  | 'water'
  | 'park'
  | 'building'
  | 'road'
  | 'roadEdge'
  | 'street'
  | 'highway'
  | 'highwayEdge'
  | 'label'
  | 'halo';

/** Which Blueprint colour (its palette step, as --bp-<step>) each part is drawn in, light and dark. */
export const MAP_COLOURS: Readonly<Record<MapTheme, Readonly<Record<MapPart, string>>>> = {
  light: {
    land: 'bg',
    water: 'rule',
    park: 'grid',
    building: 'grid',
    road: 'surface',
    roadEdge: 'rule',
    street: 'surface',
    highway: 'grid',
    highwayEdge: 'rule',
    label: 'muted',
    halo: 'bg',
  },
  dark: {
    land: 'surface',
    water: 'bg',
    park: 'grid',
    building: 'bg',
    road: 'rule',
    roadEdge: 'rule',
    street: 'grid',
    highway: 'rule',
    highwayEdge: 'stroke',
    label: 'muted',
    halo: 'surface',
  },
};

/** One rule of Google's style array (google.maps.MapTypeStyle). */
export interface MapTypeStyle {
  featureType?: string;
  elementType?: string;
  stylers: Record<string, string | number>[];
}

/** A part's colour as a hex, from the token set's palette. */
export function mapColour(theme: MapTheme, part: MapPart): string {
  const step = MAP_COLOURS[theme][part];
  const hex = blueprint.palette[theme]?.[step];
  if (!hex) throw new Error(`Blueprint has no ${theme}.${step} for the map's ${part}`);
  return hex;
}

/** Blueprint's map style for Google's map, light or dark. */
export function mapStyle(theme: MapTheme): MapTypeStyle[] {
  const c = (part: MapPart) => mapColour(theme, part);
  const off = [{ visibility: 'off' }];
  return [
    { elementType: 'geometry', stylers: [{ color: c('land') }] },
    { elementType: 'labels.icon', stylers: off },
    { elementType: 'labels.text.fill', stylers: [{ color: c('label') }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: c('halo') }, { weight: 3 }] },
    // turned off: land parcels, neighbourhood names, points of interest but park shapes, transit
    { featureType: 'administrative.land_parcel', stylers: off },
    { featureType: 'administrative.neighborhood', stylers: off },
    { featureType: 'poi', stylers: off },
    {
      featureType: 'poi.park',
      elementType: 'geometry',
      stylers: [{ visibility: 'on' }, { color: c('park') }],
    },
    { featureType: 'transit', stylers: off },
    { featureType: 'landscape.man_made', elementType: 'geometry', stylers: [{ color: c('building') }] },
    { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: c('road') }] },
    { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: c('roadEdge') }] },
    { featureType: 'road', elementType: 'labels.icon', stylers: off },
    { featureType: 'road.local', elementType: 'geometry.fill', stylers: [{ color: c('street') }] },
    { featureType: 'road.local', elementType: 'geometry.stroke', stylers: off },
    { featureType: 'road.highway', elementType: 'geometry.fill', stylers: [{ color: c('highway') }] },
    { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: c('highwayEdge') }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: c('water') }] },
    { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: c('label') }] },
  ];
}
